import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, BookOpen, CalendarClock, CalendarDays, ClipboardCheck, GitMerge, History, Lock, Mail, Package, Radar, TrendingUp, Wrench } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { DeviceHistoryCard } from '../components/device-history';
import { DueAtField } from '../components/due-at-field';
import { FloorplanPanel } from '../components/floorplan-editor';
import { TicketFilesCard } from '../components/ticket-files';
import { TicketPicturesCard } from '../components/ticket-images';
import { TicketLoanersCard } from '../components/loaner-dialog';
import { ChecklistCard, CostsCard, LocationCard } from '../components/facilities-work';
import { TicketAutomationCard, TicketRelationsCard, WatchersCard } from '../components/ticket-relations';
import { SurveyCard, TicketFieldsCard, TimeCard } from '../components/ticket-tracking';
import { useToast } from '../components/toast';
import { PriorityBadge, WorkOrderStatusBadge } from '../components/work-order-badges';
import { WorkOrderPartsModal } from '../components/WorkOrderPartsModal';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  cn,
  DescriptionList,
  ErrorState,
  Field,
  Input,
  PageHeader,
  PageLoader,
  Select,
  Switch,
  Textarea,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { indentedName, useAssignees, useDepartments } from '../lib/automation';
import { useAuth } from '../lib/auth';
import { useDesk, workOrderHref } from '../lib/desk';
import { formatDate, formatDateTime, formatDue, formatRelative } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { KnowledgePage } from '../lib/knowledge';
import { modelLabel } from '../lib/parts';
import { useSoftwareList } from '../lib/software';
import type { WorkOrderCategory, WorkOrderComment, WorkOrderDetail, WorkOrderPriority, WorkOrderStatus } from '../lib/types';
import { OPEN_WORK_ORDER_STATUSES, WORK_ORDER_CATEGORY_LABEL, WORK_ORDER_PRIORITY_LABEL, WORK_ORDER_STATUS_LABEL } from '../lib/work-orders';

type UpdateBody = Partial<{
  status: WorkOrderStatus;
  priority: WorkOrderPriority;
  category: WorkOrderCategory;
  assignedToId: string | null;
  departmentId: string | null;
  resolution: string;
  repairNeeded: boolean;
  dueAt: string | null;
  buildingId: string | null;
  roomId: string | null;
  locationDetail: string | null;
  facilityAssetId: string | null;
  softwareId: string | null;
  softwareProblemTypeId: string | null;
}>;

/** Whether a technician’s reply reached the requester by email. */
function ReplyEmailBadge({ email }: { email: NonNullable<WorkOrderComment["email"]> }) {
  if (email.status === "SENT") {
    return (
      <Badge tone="blue">
        <Mail className="size-3" /> Emailed to {email.toAddress}
      </Badge>
    );
  }
  if (email.status === "QUEUED" || email.status === "SENDING") {
    return (
      <span title={email.error ?? undefined}>
        <Badge tone={email.error ? "amber" : "gray"}>
          <Mail className="size-3" /> {email.error ? "Retrying email to" : "Emailing"} {email.toAddress}
        </Badge>
      </span>
    );
  }
  return (
    <span title={email.error ?? undefined}>
      <Badge tone="red">
        <Mail className="size-3" /> Email not sent
      </Badge>
    </span>
  );
}

export function WorkOrderDetailPage() {
  const { id } = useParams();
  const { me } = useAuth();
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const [internal, setInternal] = useState(false);
  const [resolutionDraft, setResolutionDraft] = useState<string | null>(null);
  const [partsOpen, setPartsOpen] = useState(false);

  const query = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => api.get<WorkOrderDetail>(`/work-orders/${id}`),
    // Check back while a reply is still on its way by email, and keep facilities work orders live.
    refetchInterval: (q) =>
      q.state.data?.comments.some((c) => c.email?.status === 'QUEUED' || c.email?.status === 'SENDING') ? 5000 : desk.facilities ? 30_000 : false,
  });
  const assignees = useAssignees(desk.can('TECHNICIAN'));
  const departments = useDepartments(desk.can('VIEWER'));
  const softwareList = useSoftwareList(!desk.facilities && desk.can('TECHNICIAN'));
  // Help articles the person can read about the ticket's software.
  const ticketSoftwareId = desk.facilities ? null : (query.data?.software?.id ?? null);
  const softwareArticles = useQuery({
    queryKey: ['knowledge', 'software-count', ticketSoftwareId],
    queryFn: () => api.get<KnowledgePage>('/knowledge', { softwareId: ticketSoftwareId, pageSize: 1 }),
    enabled: !!ticketSoftwareId,
    staleTime: 60_000,
  });
  const articleCount = softwareArticles.data?.total ?? 0;

  const update = useMutation({
    mutationFn: (body: UpdateBody) => api.patch(`/work-orders/${id}`, body),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success('Work order updated');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const postComment = useMutation({
    mutationFn: () => api.post(`/work-orders/${id}/comments`, { body: comment, internal }),
    onSuccess: () => {
      setComment('');
      setInternal(false);
      void queryClient.invalidateQueries({ queryKey: ['work-order', id] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const ticket = query.data;
  // A link to a ticket on the other help desk opens it where it belongs.
  if (ticket.service !== desk.service) return <Navigate to={workOrderHref(ticket.service, ticket.id)} replace />;

  const facilities = desk.facilities;
  const { permissions } = ticket;
  const staff = permissions.viewAsset;
  const resolution = resolutionDraft ?? ticket.resolution ?? '';
  const assigneeOptions = [...(assignees.data ?? [])];
  if (ticket.assignedTo && !assigneeOptions.some((a) => a.id === ticket.assignedTo!.id)) {
    assigneeOptions.push({ id: ticket.assignedTo.id, name: ticket.assignedTo.name, email: '' });
  }
  const asset = ticket.asset;
  const software = facilities ? null : ticket.software;
  // The problems the ticket's software keeps a list of; without one, the ticket uses the device categories.
  const ticketProblems = software ? (softwareList.data?.find((entry) => entry.id === software.id)?.problemTypes ?? []) : [];
  // Software no longer offered stays listed on tickets already about it.
  const softwareOptions = softwareList.data ?? [];
  const makeAndModel =
    asset?.model && asset.manufacturer ? modelLabel({ name: asset.model, manufacturer: { name: asset.manufacturer } }) : (asset?.model ?? asset?.manufacturer);
  const overdue = !!ticket.dueAt && new Date(ticket.dueAt) < new Date() && OPEN_WORK_ORDER_STATUSES.includes(ticket.status);
  // On the technology help desk only staff see when work is due.
  const showDue = facilities || staff;
  const departmentLabel = facilities ? 'Category' : 'Department';

  const submitComment = (event: FormEvent) => {
    event.preventDefault();
    if (comment.trim()) postComment.mutate();
  };

  return (
    <>
      <PageHeader
        back={staff ? { to: `${desk.base}/work-orders`, label: 'Work orders' } : { to: `${desk.base}/requests`, label: facilities ? 'My facilities requests' : 'My requests' }}
        title={
          <>
            <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
          </>
        }
        meta={
          <>
            <WorkOrderStatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            {!facilities && <Badge>{WORK_ORDER_CATEGORY_LABEL[ticket.category]}</Badge>}
            {staff && ticket.departmentPath && <Badge tone="brand">{ticket.departmentPath}</Badge>}
            {showDue && ticket.dueAt && OPEN_WORK_ORDER_STATUSES.includes(ticket.status) && (
              <Badge tone={overdue ? 'red' : 'amber'}>{overdue ? `Overdue since ${formatDue(ticket.dueAt)}` : `Due ${formatDue(ticket.dueAt)}`}</Badge>
            )}
            {staff && ticket.escalationLevel > 0 && (
              <Badge tone="red">
                <TrendingUp className="size-3" /> Escalated{ticket.escalationLevel > 1 ? ` ×${ticket.escalationLevel}` : ''}
              </Badge>
            )}
            {!facilities && ticket.repairNeeded && staff && (
              <Badge tone="amber">
                <Wrench className="size-3" /> Repair needed
              </Badge>
            )}
            {ticket.eventRequest && (
              <Link to={`/events/${ticket.eventRequest.id}`}>
                <Badge tone="violet">
                  <CalendarDays className="size-3" /> Event: {ticket.eventRequest.title}
                </Badge>
              </Link>
            )}
            {staff && ticket.smartRule && (
              <Badge tone="violet">
                <Radar className="size-3" /> Smart rule: {ticket.smartRule.name}
              </Badge>
            )}
            {staff && ticket.recurringTicket && (
              <Badge tone="violet">
                <CalendarClock className="size-3" /> Scheduled: {ticket.recurringTicket.name}
              </Badge>
            )}
            {staff && ticket.facilitySchedule && (
              <Link to="/facilities/maintenance?tab=schedules">
                <Badge tone="violet">
                  <CalendarClock className="size-3" /> Preventive maintenance: {ticket.facilitySchedule.name}
                </Badge>
              </Link>
            )}
            {staff && ticket.inspection && (
              <Link to={`/facilities/inspections/${ticket.inspection.id}`}>
                <Badge tone="violet">
                  <ClipboardCheck className="size-3" /> Inspection: {ticket.inspection.name}
                </Badge>
              </Link>
            )}
          </>
        }
      />

      {ticket.mergedInto && (
        <Alert tone="blue" icon={<GitMerge />} className="mb-6">
          {staff ? (
            <>
              This ticket was merged into{' '}
              <Link to={desk.workOrderHref(ticket.mergedInto.id)} className="font-medium underline">
                #{ticket.mergedInto.number} {ticket.mergedInto.title}
              </Link>
              . Work continues there.
            </>
          ) : (
            <>This problem was already reported, so it’s being handled on ticket #{ticket.mergedInto.number}.</>
          )}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {ticket.survey?.canRespond && <SurveyCard ticket={ticket} />}

          <Card>
            <CardHeader
              title={facilities ? 'Request' : 'Problem'}
              description={`${ticket.source === 'EMAIL' ? 'Emailed' : ticket.source === 'PREVENTIVE' || ticket.source === 'INSPECTION' ? 'Opened for' : 'Reported'} by ${ticket.requesterName} · ${formatDateTime(ticket.createdAt)}`}
            />
            <p className="px-5 py-4 text-sm whitespace-pre-wrap text-slate-700">{ticket.description}</p>
            {ticket.resolution && !permissions.update && (
              <div className="border-t border-slate-100 bg-emerald-50/50 px-5 py-4">
                <p className="text-xs font-semibold tracking-wide text-emerald-800 uppercase">Resolution</p>
                <p className="mt-1 text-sm whitespace-pre-wrap text-slate-700">{ticket.resolution}</p>
              </div>
            )}
          </Card>

          {(ticket.images.length > 0 || permissions.addImages) && <TicketPicturesCard ticket={ticket} />}
          {(ticket.files.length > 0 || permissions.addFiles) && <TicketFilesCard ticket={ticket} />}

          {facilities && staff && <ChecklistCard ticket={ticket} />}

          {staff && <TicketRelationsCard ticket={ticket} />}

          <Card>
            <CardHeader title="Activity" />
            <ol className="space-y-3 p-5">
              {ticket.comments.length === 0 && <li className="text-sm text-slate-500">No updates yet.</li>}
              {ticket.comments.map((entry) =>
                entry.system ? (
                  <li key={entry.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {entry.internal ? <Lock className="size-3.5" /> : <History className="size-3.5" />}
                    <span>
                      <span className="font-medium text-slate-700">{entry.authorName}</span> · {entry.body}
                    </span>
                    <span className="text-slate-400">{formatRelative(entry.createdAt)}</span>
                  </li>
                ) : (
                  <li
                    key={entry.id}
                    className={cn('rounded-lg border px-4 py-3', entry.internal ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-white')}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="font-medium text-slate-800">{entry.authorName}</span>
                      {entry.internal && (
                        <Badge tone="amber">
                          <Lock className="size-3" /> Internal note
                        </Badge>
                      )}
                      {entry.email && <ReplyEmailBadge email={entry.email} />}
                      <span className="ml-auto">{formatDateTime(entry.createdAt)}</span>
                    </div>
                    <p className="mt-1 text-sm whitespace-pre-wrap text-slate-700">{entry.body}</p>
                  </li>
                ),
              )}
            </ol>
            {permissions.comment && (
              <form onSubmit={submitComment} className="space-y-3 border-t border-slate-100 p-5">
                <Textarea
                  rows={3}
                  value={comment}
                  maxLength={5000}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={permissions.internalNotes ? 'Post an update for the requester, or an internal note for staff' : `Add a comment for ${desk.team}`}
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {permissions.internalNotes ? (
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} className="size-4 rounded border-slate-300" />
                        Internal note (staff only)
                      </label>
                      {ticket.emailReplies && (
                        <p className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Mail className="size-3.5" />
                          {internal ? "Internal notes aren’t emailed." : `Updates are emailed to ${ticket.emailReplies.to}.`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span />
                  )}
                  <Button type="submit" variant="primary" disabled={!comment.trim()} loading={postComment.isPending}>
                    {internal ? 'Add note' : 'Post update'}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Details" />
            <div className="space-y-4 p-5">
              {permissions.update ? (
                <>
                  <Field label="Status">
                    <Select value={ticket.status} onChange={(e) => update.mutate({ status: e.target.value as WorkOrderStatus })}>
                      {(Object.keys(WORK_ORDER_STATUS_LABEL) as WorkOrderStatus[]).map((status) => (
                        <option key={status} value={status}>
                          {WORK_ORDER_STATUS_LABEL[status]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={departmentLabel}>
                    <Select value={ticket.departmentId ?? ''} onChange={(e) => update.mutate({ departmentId: e.target.value || null })}>
                      <option value="">No {desk.departmentWord}</option>
                      {departments.data?.map((department) => (
                        <option key={department.id} value={department.id}>
                          {indentedName(department)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Assigned to">
                    <Select value={ticket.assignedToId ?? ''} onChange={(e) => update.mutate({ assignedToId: e.target.value || null })}>
                      <option value="">Unassigned</option>
                      {assigneeOptions.map((assignee) => (
                        <option key={assignee.id} value={assignee.id}>
                          {assignee.name}
                        </option>
                      ))}
                    </Select>
                    {me && ticket.assignedToId !== me.user.id && (
                      <button type="button" onClick={() => update.mutate({ assignedToId: me.user.id })} className="mt-1 text-xs font-medium text-brand-700 hover:underline">
                        Assign to me
                      </button>
                    )}
                  </Field>
                  <Field label="Priority">
                    <Select value={ticket.priority} onChange={(e) => update.mutate({ priority: e.target.value as WorkOrderPriority })}>
                      {(Object.keys(WORK_ORDER_PRIORITY_LABEL) as WorkOrderPriority[]).map((priority) => (
                        <option key={priority} value={priority}>
                          {WORK_ORDER_PRIORITY_LABEL[priority]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {!facilities && (
                    <Field label="Type of problem">
                      {ticketProblems.length > 0 ? (
                        <Select
                          value={ticket.softwareProblemType?.id ?? ''}
                          onChange={(e) => update.mutate({ softwareProblemTypeId: e.target.value || null })}
                        >
                          <option value="">Not said</option>
                          {ticketProblems.map((problem) => (
                            <option key={problem.id} value={problem.id}>
                              {problem.name}
                              {problem.offered ? '' : ' (not offered)'}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Select value={ticket.category} onChange={(e) => update.mutate({ category: e.target.value as WorkOrderCategory })}>
                          {(Object.keys(WORK_ORDER_CATEGORY_LABEL) as WorkOrderCategory[]).map((category) => (
                            <option key={category} value={category}>
                              {WORK_ORDER_CATEGORY_LABEL[category]}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  )}
                  {!facilities && (
                    <Field label="Software">
                      <Select value={ticket.softwareId ?? ''} onChange={(e) => update.mutate({ softwareId: e.target.value || null })}>
                        <option value="">None</option>
                        {software && !softwareOptions.some((entry) => entry.id === software.id) && <option value={software.id}>{software.name}</option>}
                        {softwareOptions.map((entry) => (
                          <option key={entry.id} value={entry.id}>
                            {entry.name}
                            {entry.offered ? '' : ' (not offered)'}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  <DueAtField dueAt={ticket.dueAt} overdue={overdue} saving={update.isPending} onSave={(dueAt) => update.mutate({ dueAt })} />
                  {!facilities && asset && (
                    <Switch
                      checked={ticket.repairNeeded}
                      onChange={(repairNeeded) => update.mutate({ repairNeeded })}
                      disabled={update.isPending}
                      label="Physical repair needed"
                      description="Lists parts that fit this device"
                    />
                  )}
                  <Field label="Resolution" hint="Visible to the requester">
                    <Textarea rows={3} value={resolution} maxLength={5000} onChange={(e) => setResolutionDraft(e.target.value)} />
                    {resolutionDraft !== null && resolutionDraft !== (ticket.resolution ?? '') && (
                      <Button
                        size="sm"
                        className="mt-2"
                        loading={update.isPending}
                        onClick={() => update.mutate({ resolution: resolutionDraft }, { onSuccess: () => setResolutionDraft(null) })}
                      >
                        Save resolution
                      </Button>
                    )}
                  </Field>
                </>
              ) : (
                <DescriptionList
                  columns={1}
                  items={[
                    { label: 'Status', value: <WorkOrderStatusBadge status={ticket.status} /> },
                    ...(staff ? [{ label: departmentLabel, value: ticket.departmentPath ?? 'None' }] : []),
                    { label: 'Assigned to', value: ticket.assignedTo?.name ?? 'Not assigned yet' },
                    { label: 'Priority', value: WORK_ORDER_PRIORITY_LABEL[ticket.priority] },
                    ...(showDue && ticket.dueAt ? [{ label: 'Due', value: formatDue(ticket.dueAt) }] : []),
                    { label: 'Last update', value: formatRelative(ticket.updatedAt) },
                  ]}
                />
              )}
            </div>
          </Card>

          {facilities && (
            <LocationCard ticket={ticket} saving={update.isPending} onUpdate={(body, done) => update.mutate(body, { onSuccess: done })} />
          )}
          {facilities && staff && <CostsCard ticketId={ticket.id} />}
          {staff && <TicketFieldsCard ticket={ticket} />}
          {staff && <TimeCard ticket={ticket} />}
          {ticket.survey && !ticket.survey.canRespond && <SurveyCard ticket={ticket} />}

          {software && (
            <Card>
              <CardHeader title="Software" />
              <div className="flex items-start gap-3 p-5 text-sm">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <AppWindow className="size-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{software.name}</p>
                  {software.publisher && <p className="text-xs text-slate-500">{software.publisher}</p>}
                  {articleCount > 0 && (
                    <Link to={`/knowledge?softwareId=${software.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline">
                      <BookOpen className="size-3.5" /> {articleCount === 1 ? '1 help article' : `${articleCount} help articles`} about it
                    </Link>
                  )}
                </div>
              </div>
            </Card>
          )}

          {!facilities && (asset || !software) && (
            <Card>
              <CardHeader title="Device" />
              <div className="space-y-1 p-5 text-sm">
                {asset ? (
                  <>
                    {staff ? (
                      <Link to={`/assets/${asset.id}`} className="font-mono font-semibold text-brand-800 hover:underline">
                        {asset.assetTag}
                      </Link>
                    ) : (
                      <p className="font-mono font-semibold text-slate-900">{asset.assetTag}</p>
                    )}
                    <p className="text-slate-700">{asset.name}</p>
                    {makeAndModel && makeAndModel !== asset.name && <p className="text-xs text-slate-500">{makeAndModel}</p>}
                    {asset.serialNumber && <p className="text-xs text-slate-500">Serial {asset.serialNumber}</p>}
                  </>
                ) : (
                  <p className="text-slate-500">This ticket isn’t about a specific device.</p>
                )}
                {ticket.room && (
                  <p className="pt-2 text-xs text-slate-500">
                    Room:{' '}
                    {staff ? (
                      <Link to={`/locations/rooms/${ticket.room.id}`} className="text-brand-700 hover:underline">
                        {ticket.room.name}
                      </Link>
                    ) : (
                      ticket.room.name
                    )}{' '}
                    · {ticket.room.building.name}
                  </p>
                )}
                {asset && ticket.repairNeeded && permissions.viewParts && (
                  <Button variant="subtle" icon={<Package />} className="mt-3 w-full" onClick={() => setPartsOpen(true)}>
                    View available parts
                  </Button>
                )}
              </div>
            </Card>
          )}

          {!facilities && asset && permissions.deviceHistory && (
            <DeviceHistoryCard asset={asset} workOrder={{ id: ticket.id, number: ticket.number }} compact />
          )}

          {ticket.eventRequest?.floorplan && (
            <Card>
              <CardHeader
                title="Event floorplan"
                description="How the requester wants the space set up"
                actions={
                  <Link to={`/events/${ticket.eventRequest.id}`} className="text-sm font-medium text-brand-700 hover:underline">
                    Open the event
                  </Link>
                }
              />
              <div className="p-5">
                <FloorplanPanel plan={ticket.eventRequest.floorplan} canEdit={false} name={ticket.eventRequest.title} />
              </div>
            </Card>
          )}

          {!facilities && permissions.lendLoaner && <TicketLoanersCard ticket={ticket} loaners={ticket.loaners} />}

          <Card>
            <CardHeader title="People" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  {
                    label: 'Reported by',
                    value: (
                      <>
                        {ticket.requesterPerson && staff && !facilities ? (
                          <Link to={`/people/${ticket.requesterPerson.id}`} className="text-brand-700 hover:underline">
                            {ticket.requesterName}
                          </Link>
                        ) : (
                          ticket.requesterName
                        )}
                        {ticket.requesterEmail && <span className="block text-xs text-slate-500">{ticket.requesterEmail}</span>}
                      </>
                    ),
                  },
                  {
                    label: 'Submitted by',
                    value: ticket.createdBy && ticket.createdBy.name !== ticket.requesterName ? ticket.createdBy.name : null,
                  },
                  { label: 'Opened', value: formatDateTime(ticket.createdAt) },
                  { label: 'First response', value: staff && ticket.firstResponseAt ? formatDateTime(ticket.firstResponseAt) : null },
                  { label: 'Resolved', value: ticket.resolvedAt && formatDateTime(ticket.resolvedAt) },
                  { label: 'Resolved by', value: staff && ticket.resolvedAt ? (ticket.resolvedBy?.name ?? 'Automation') : null },
                ].filter((item) => item.value)}
              />
            </div>
          </Card>

          {permissions.watch && <WatchersCard ticket={ticket} />}
          {!facilities && permissions.update && <TicketAutomationCard ticketId={ticket.id} />}
        </div>
      </div>

      {!facilities && asset && permissions.viewParts && (
        <WorkOrderPartsModal open={partsOpen} onClose={() => setPartsOpen(false)} workOrderId={ticket.id} canUse={permissions.useParts} />
      )}
    </>
  );
}
